/**
 * TenantSetupSection (Phase 44.8).
 *
 * Settings-page section that surfaces the current tenant's status + a
 * Re-open setup affordance for tenant-admins when the tenant is active.
 *
 * Behaviour:
 *   - visible only to tenant-admin / super-admin (gated by the
 *     `tenantStatus.role` from the Phase 43.4 store slice)
 *   - hidden when the tenant is already in `setup_review` — the banner
 *     + the /setup redirect already cover that case
 *   - Re-open asks for confirmation, fires the 44.1 reopen endpoint,
 *     redirects to /setup on success
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store/useStore.js";
import { reopen as reopenSetup } from "../services/setup.js";

const STATUS_COPY = {
  active: "Your workspace is active. Pipelines are running as normal.",
  setup_review: "Setup is open — finish and activate to start running pipelines.",
  provisioning: "Your workspace is still being provisioned. Come back in a few minutes.",
  suspended: "Your workspace has been suspended. Contact your super-admin.",
};

export default function TenantSetupSection() {
  const navigate = useNavigate();
  const tenantStatus = useStore((s) => s.tenantStatus);
  const loadTenantStatus = useStore((s) => s.loadTenantStatus);
  const pushToast = useStore((s) => s.pushToast);
  const [submitting, setSubmitting] = useState(false);

  if (!tenantStatus) return null;
  // Platform-only super-admin (no tenant selected via the switcher):
  // hide the section entirely — status/role wouldn't be actionable.
  if (!tenantStatus.tenantId || !tenantStatus.status) return null;
  const role = tenantStatus.role;
  if (role !== "tenant-admin" && role !== "super-admin") return null;

  const status = tenantStatus.status || "unknown";
  const canReopen = status === "active";

  const handleReopen = async () => {
    const confirmed = window.confirm(
      "Re-opening setup will pause pipelines for your workspace until you reactivate. Continue?"
    );
    if (!confirmed) return;
    setSubmitting(true);
    try {
      await reopenSetup();
      // Force-refresh the tenantStatus so the guard redirects us to
      // /setup on the next navigation (and the banner updates).
      await loadTenantStatus({ force: true });
      pushToast("Setup re-opened — pipelines paused until reactivation.");
      navigate("/setup", { replace: true });
    } catch (err) {
      pushToast(`Couldn't re-open setup: ${err.message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="settings-section">
      <h2>Tenant setup</h2>
      <div className="settings-card">
        <div className="settings-row">
          <span className="settings-label">Status</span>
          <span className="settings-value">
            <span className={`tenant-setup__status tenant-setup__status--${status}`}>
              {status}
            </span>
          </span>
        </div>
        <p className="settings-description">{STATUS_COPY[status] || "Status unknown."}</p>
        {canReopen && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReopen}
            disabled={submitting}
          >
            {submitting ? "Re-opening…" : "Re-open setup"}
          </button>
        )}
        {status === "setup_review" && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/setup")}
          >
            Continue setup
          </button>
        )}
      </div>
    </section>
  );
}
